import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';
import { InstallButton } from '@/context/InstallContext';
import { formatDate } from '@/utils/dateFormat';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [vehicles, setVehicles] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [ratingData, setRatingData] = useState({
    skills_rating: 5,
    conduct_rating: 5,
    neatness_rating: 5,
    comment: ''
  });
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: ''
  });
  const [servicingInfoOpen, setServicingInfoOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const APP_URL = window.location.origin;
  const INVITE_MESSAGE = `Join Excel Era Auto Care for professional vehicle servicing at your doorstep! Register here: ${APP_URL}/register`;

  const handleShare = async (platform) => {
    const shareUrl = `${APP_URL}/register`;
    const shareText = INVITE_MESSAGE;
    
    switch(platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'sms':
        window.open(`sms:?body=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent('Join Excel Era Auto Care')}&body=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareText);
        toast.success('Invite link copied!');
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'Excel Era Auto Care',
              text: shareText,
              url: shareUrl
            });
          } catch (err) {
            console.log('Share cancelled');
          }
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, appointmentsRes, historyRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/appointments`),
        axios.get(`${API}/service-history`)
      ]);
      setVehicles(vehiclesRes.data);
      setAppointments(appointmentsRes.data);
      setServiceHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`);
      toast.success('Vehicle deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const getNextServiceDays = (nextServiceDate) => {
    if (!nextServiceDate) return null;
    const days = differenceInDays(parseISO(nextServiceDate), new Date());
    return days;
  };

  const calculatePrice = () => {
    if (vehicles.length === 0) return 10000;
    return 10000 + (vehicles.length - 1) * 8000;  // ₦8,000 per additional vehicle
  };

  const printServiceHistory = () => {
    const printWindow = window.open('', '_blank');
    const content = `
      <html>
        <head>
          <title>Service History - ${user?.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1A3A52; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #1A3A52; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            .header { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Excel Era Auto Care - Service History</h1>
            <p><strong>Customer:</strong> ${user?.name}</p>
            <p><strong>Phone:</strong> ${user?.phone}</p>
            <p><strong>Email:</strong> ${user?.email}</p>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Service Type</th>
                <th>Vehicle</th>
                <th>Technician</th>
                <th>Location</th>
                <th>Oil/Filter</th>
              </tr>
            </thead>
            <tbody>
              ${serviceHistory.map(service => `
                <tr>
                  <td>${service.date}</td>
                  <td>${service.service_type}</td>
                  <td>${service.vehicle ? `${service.vehicle.make} ${service.vehicle.model} (${service.vehicle.license_plate})` : 'N/A'}</td>
                  <td>${service.technician || 'N/A'}</td>
                  <td>${service.location || 'N/A'}</td>
                  <td>${service.has_own_oil ? 'Own Oil' : service.preferred_oil_type || 'Provided'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const copyServiceHistory = () => {
    const text = `
EXCEL ERA AUTO CARE - SERVICE HISTORY
=====================================
Customer: ${user?.name}
Phone: ${user?.phone}
Email: ${user?.email}
Generated: ${new Date().toLocaleString()}

${serviceHistory.map((service, index) => `
${index + 1}. ${service.service_type} - ${service.date}
   Vehicle: ${service.vehicle ? `${service.vehicle.make} ${service.vehicle.model} (${service.vehicle.license_plate})` : 'N/A'}
   Technician: ${service.technician || 'N/A'}
   Location: ${service.location || 'N/A'}
   Oil/Filter: ${service.has_own_oil ? 'Own Oil' : service.preferred_oil_type || 'Provided'}
   ${service.notes ? `Notes: ${service.notes}` : ''}
`).join('\n')}
    `.trim();
    
    navigator.clipboard.writeText(text);
    toast.success('Service history copied to clipboard!');
  };

  const openRatingDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setRatingData({
      skills_rating: 5,
      conduct_rating: 5,
      neatness_rating: 5,
      comment: ''
    });
    setRatingDialogOpen(true);
  };

  const submitRating = async () => {
    try {
      await axios.post(`${API}/appointments/${selectedAppointment.id}/rate`, ratingData);
      toast.success('Rating submitted successfully!');
      setRatingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };

  const canReschedule = (appointment) => {
    if (appointment.status === 'completed' || appointment.status === 'cancelled') {
      return false;
    }
    
    const appointmentDate = new Date(appointment.date + 'T' + appointment.time);
    const now = new Date();
    const hoursDifference = (appointmentDate - now) / (1000 * 60 * 60);
    
    return hoursDifference > 24;
  };

  const openRescheduleDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.date,
      time: appointment.time
    });
    setRescheduleDialogOpen(true);
  };

  const submitReschedule = async () => {
    try {
      await axios.put(`${API}/appointments/${selectedAppointment.id}/reschedule`, rescheduleData);
      toast.success('Appointment rescheduled successfully!');
      setRescheduleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule appointment');
    }
  };

  const StarRating = ({ value, onChange, label }) => (
    <div>
      <Label className="font-bold uppercase text-xs tracking-wide mb-2 block">{label}</Label>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="transition-transform hover:scale-110"
          >
            <svg
              className={`w-8 h-8 ${star <= value ? 'text-yellow-500' : 'text-gray-300'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-xl font-heading text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-heading font-bold text-foreground">EXCEL ERA</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setInviteDialogOpen(true)}
                className="font-bold uppercase text-xs border-2 border-green-600 text-green-600 hover:bg-green-50"
                data-testid="invite-btn"
              >
                Invite Friends
              </Button>
              <InstallButton variant="default" size="sm" />
              <span className="text-sm font-medium text-muted-foreground hidden sm:inline">Hello, {user?.name}</span>
              <Button 
                variant="ghost" 
                onClick={logout}
                data-testid="logout-btn"
                className="font-bold uppercase text-sm"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Banner */}
        <div className="bg-primary text-white p-8 rounded-sm shadow-hard mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-heading font-bold mb-2">Welcome back, {user?.name}!</h1>
              <p className="text-lg opacity-90">Manage your vehicles and service appointments</p>
            </div>
            <Button
              onClick={() => setServicingInfoOpen(true)}
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/50 font-bold uppercase text-sm px-6 py-3 h-auto whitespace-nowrap"
              data-testid="know-more-btn"
            >
              Know More About Servicing
            </Button>
          </div>
        </div>

        {/* Service Countdown Section */}
        {vehicles.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Service Countdown</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((vehicle) => {
                const daysUntil = getNextServiceDays(vehicle.next_service_date);
                const isOverdue = daysUntil !== null && daysUntil < 0;
                const isDueSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 30;
                const isDueVerySoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
                
                return (
                  <div 
                    key={vehicle.id} 
                    className={`p-5 rounded-sm border-2 ${
                      isOverdue ? 'bg-red-50 border-red-300' :
                      isDueVerySoon ? 'bg-orange-50 border-orange-300' :
                      isDueSoon ? 'bg-yellow-50 border-yellow-300' :
                      vehicle.next_service_date ? 'bg-green-50 border-green-300' :
                      'bg-gray-50 border-gray-200'
                    }`}
                    data-testid="service-countdown-card"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-heading font-bold text-foreground">{vehicle.make} {vehicle.model}</h3>
                        <p className="text-xs text-muted-foreground font-mono">{vehicle.license_plate}</p>
                      </div>
                      {vehicle.last_oil_type && (
                        <span className="text-xs bg-white px-2 py-1 rounded border font-medium">{vehicle.last_oil_type}</span>
                      )}
                    </div>
                    
                    {vehicle.next_service_date ? (
                      <div className="space-y-2">
                        <div className={`text-3xl font-heading font-black ${
                          isOverdue ? 'text-red-600' :
                          isDueVerySoon ? 'text-orange-600' :
                          isDueSoon ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {isOverdue ? (
                            <span>{Math.abs(daysUntil)} days overdue</span>
                          ) : (
                            <span>{daysUntil} days</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {isOverdue ? 'Service was due on' : 'Until next service on'}{' '}
                          <span className="font-bold">{format(parseISO(vehicle.next_service_date), 'dd/MM/yyyy')}</span>
                        </p>
                        {vehicle.last_service_date && (
                          <p className="text-xs text-muted-foreground">
                            Last service: {format(parseISO(vehicle.last_service_date), 'dd/MM/yyyy')}
                          </p>
                        )}
                        {(isOverdue || isDueVerySoon) && (
                          <Button 
                            size="sm" 
                            onClick={() => navigate('/appointments/book')}
                            className={isOverdue ? 'bg-red-600 hover:bg-red-700 text-white w-full mt-2' : 'bg-orange-600 hover:bg-orange-700 text-white w-full mt-2'}
                          >
                            Book Service Now
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className="text-sm text-muted-foreground mb-2">No service history yet</p>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate('/appointments/book')}
                          className="w-full"
                        >
                          Book First Service
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
            <div className="text-4xl font-heading font-black text-primary mb-2">{vehicles.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Registered Vehicles</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
            <div className="text-4xl font-heading font-black text-secondary mb-2">{appointments.length}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Total Appointments</div>
          </div>
          <div className="bg-white border-2 border-border p-6 rounded-sm shadow-sm">
            <div className="text-4xl font-heading font-black text-foreground mb-2 font-mono">₦{calculatePrice().toLocaleString()}</div>
            <div className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Service Cost</div>
          </div>
        </div>

        {/* My Appointments - PRIORITY SECTION */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-heading font-bold text-foreground">My Appointments</h2>
            {vehicles.length > 0 && (
              <Button 
                onClick={() => navigate('/appointments/book')}
                data-testid="quick-book-btn"
                className="btn-primary"
              >
                + Book New Service
              </Button>
            )}
          </div>
          
          {appointments.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
              <p className="text-xl font-medium text-muted-foreground mb-4">No appointments scheduled</p>
              {vehicles.length > 0 && (
                <Button 
                  onClick={() => navigate('/appointments/book')}
                  className="btn-primary"
                  data-testid="empty-book-btn"
                >
                  Book Your First Service
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {appointments.map((appointment) => {
                const vehicle = vehicles.find(v => v.id === appointment.vehicle_id);
                const canRate = appointment.status === 'completed' && appointment.technician_id;
                
                return (
                  <div key={appointment.id} className="bg-white border-2 border-border p-6 rounded-sm shadow-sm" data-testid="appointment-card">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-heading font-bold text-foreground">{appointment.service_type}</h3>
                          <span className={`text-xs font-bold px-3 py-1 rounded-sm uppercase ${
                            appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            appointment.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                            appointment.status === 'completed' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`} data-testid="appointment-status-badge">
                            {appointment.status}
                          </span>
                        </div>
                        {vehicle && (
                          <p className="text-sm text-muted-foreground font-medium mb-2">
                            {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                          </p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm font-medium">
                          <span>
                            <span className="text-muted-foreground">Date:</span>{' '}
                            <span className="font-bold text-foreground">{formatDate(appointment.date)}</span>
                          </span>
                          <span>
                            <span className="text-muted-foreground">Time:</span>{' '}
                            <span className="font-bold text-foreground">{appointment.time}</span>
                          </span>
                        </div>
                        {appointment.notes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">"{appointment.notes}"</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {canRate && (
                          <Button 
                            onClick={() => openRatingDialog(appointment)}
                            data-testid="rate-technician-btn"
                            className="btn-secondary"
                          >
                            Rate Technician
                          </Button>
                        )}
                        {canReschedule(appointment) && (
                          <Button 
                            onClick={() => openRescheduleDialog(appointment)}
                            data-testid="reschedule-btn"
                            variant="outline"
                            className="border-2 border-primary text-primary hover:bg-primary/5 font-bold uppercase text-sm"
                          >
                            Reschedule
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Payment Notice */}
        {vehicles.length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-200 p-6 rounded-sm mb-12">
            <div className="flex gap-4">
              <svg className="w-8 h-8 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-heading font-bold text-lg text-yellow-800 mb-2">Payment Information</h3>
                <ul className="space-y-1 text-sm text-yellow-700">
                  <li className="flex items-start gap-2">
                    <span className="font-bold mt-1">•</span>
                    <span><strong>Service Payment:</strong> Pay technician upon arrival before service begins (₦10,000 first vehicle, ₦8,000 additional)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold mt-1">•</span>
                    <span><strong>Engine Oil & Filter:</strong> If we provide oil/filter, payment is made to technician before service</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* My Vehicles */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-heading font-bold text-foreground">My Vehicles</h2>
            <Button 
              onClick={() => navigate('/vehicles/add')}
              data-testid="add-vehicle-btn"
              className="btn-primary"
            >
              + Add Vehicle
            </Button>
          </div>

          {vehicles.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
              <p className="text-xl font-medium text-muted-foreground mb-4">No vehicles registered yet</p>
              <Button onClick={() => navigate('/vehicles/add')} className="btn-primary" data-testid="empty-add-vehicle-btn">
                Add Your First Vehicle
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => {
                const daysUntilService = getNextServiceDays(vehicle.next_service_date);
                const isOverdue = daysUntilService !== null && daysUntilService < 0;
                const isDueSoon = daysUntilService !== null && daysUntilService >= 0 && daysUntilService <= 30;

                return (
                  <div key={vehicle.id} className="card-vehicle" data-testid="vehicle-card">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-heading font-bold text-foreground">
                          {vehicle.make} {vehicle.model}
                        </h3>
                        <p className="text-sm text-muted-foreground font-medium">{vehicle.year}</p>
                      </div>
                      {isOverdue && (
                        <span className="bg-secondary text-white text-xs font-bold px-2 py-1 rounded-sm uppercase" data-testid="overdue-badge">
                          Overdue
                        </span>
                      )}
                      {isDueSoon && (
                        <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-sm uppercase" data-testid="due-soon-badge">
                          Due Soon
                        </span>
                      )}
                    </div>
                    <div className="space-y-2 mb-4">
                      <p className="text-sm font-medium">
                        <span className="text-muted-foreground">License:</span>{' '}
                        <span className="font-mono font-bold text-foreground">{vehicle.license_plate}</span>
                      </p>
                      {vehicle.current_mileage && (
                        <p className="text-sm font-medium">
                          <span className="text-muted-foreground">Mileage:</span>{' '}
                          <span className="font-bold text-blue-600">{vehicle.current_mileage.toLocaleString()} km</span>
                        </p>
                      )}
                      {vehicle.last_oil_type && (
                        <p className="text-sm font-medium">
                          <span className="text-muted-foreground">Oil Type:</span>{' '}
                          <span className="font-medium text-green-600">{vehicle.last_oil_type}</span>
                        </p>
                      )}
                      {vehicle.vin && (
                        <p className="text-sm font-medium">
                          <span className="text-muted-foreground">VIN:</span>{' '}
                          <span className="font-mono text-foreground">{vehicle.vin}</span>
                        </p>
                      )}
                      {vehicle.next_service_date && (
                        <p className="text-sm font-medium">
                          <span className="text-muted-foreground">Next Service:</span>{' '}
                          <span className="font-bold text-foreground">
                            {format(parseISO(vehicle.next_service_date), 'dd/MM/yyyy')}
                            {daysUntilService !== null && (
                              <span className="ml-2 text-xs">
                                ({daysUntilService > 0 ? `in ${daysUntilService} days` : `${Math.abs(daysUntilService)} days overdue`})
                              </span>
                            )}
                          </span>
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/vehicles/edit/${vehicle.id}`)}
                        data-testid="edit-vehicle-btn"
                        className="flex-1 font-bold uppercase text-xs"
                      >
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => deleteVehicle(vehicle.id)}
                        data-testid="delete-vehicle-btn"
                        className="flex-1 font-bold uppercase text-xs"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Book Service CTA */}
        {vehicles.length > 0 && (
          <div className="bg-secondary text-white p-8 rounded-sm shadow-hard mb-12 text-center">
            <h2 className="text-2xl font-heading font-bold mb-4">Need Service?</h2>
            <p className="text-lg mb-6 opacity-90">Book an appointment with our expert technicians</p>
            <Button 
              onClick={() => navigate('/appointments/book')}
              data-testid="book-appointment-cta-btn"
              className="bg-white text-secondary hover:bg-white/90 font-bold uppercase tracking-wide px-8 py-3 h-auto text-lg"
            >
              Book Appointment
            </Button>
          </div>
        )}

        {/* Service History */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-heading font-bold text-foreground">Service History</h2>
            {serviceHistory.length > 0 && (
              <div className="flex gap-3">
                <Button
                  onClick={printServiceHistory}
                  data-testid="print-history-btn"
                  className="btn-secondary flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </Button>
                <Button
                  onClick={copyServiceHistory}
                  data-testid="copy-history-btn"
                  className="btn-secondary flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </Button>
              </div>
            )}
          </div>

          {serviceHistory.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-border p-12 rounded-sm text-center">
              <p className="text-xl font-medium text-muted-foreground">No service history yet</p>
            </div>
          ) : (
            <div className="bg-white border-2 border-border rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="service-history-table">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Date</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Service</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Vehicle</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Technician</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Location</th>
                      <th className="px-6 py-4 text-left font-bold uppercase text-sm tracking-wide">Oil/Filter</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {serviceHistory.map((service, index) => (
                      <tr key={index} className="hover:bg-accent transition-colors">
                        <td className="px-6 py-4 font-bold text-foreground">{formatDate(service.date)}</td>
                        <td className="px-6 py-4 text-foreground">{service.service_type}</td>
                        <td className="px-6 py-4 text-sm text-foreground">
                          {service.vehicle ? (
                            <div>
                              <div className="font-bold">{service.vehicle.make} {service.vehicle.model}</div>
                              <div className="text-xs text-muted-foreground font-mono">{service.vehicle.license_plate}</div>
                            </div>
                          ) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-foreground">{service.technician || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">{service.location || 'N/A'}</td>
                        <td className="px-6 py-4 text-sm">
                          {service.has_own_oil ? (
                            <span className="text-green-700 font-medium">Own Oil</span>
                          ) : (
                            <span className="text-blue-700 font-medium">{service.preferred_oil_type || 'Provided'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rating Dialog */}
      <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-foreground">Rate Technician</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-6">
              <div className="bg-accent p-4 rounded-sm">
                <p className="text-sm font-medium text-muted-foreground mb-1">Service Completed</p>
                <p className="font-heading font-bold text-foreground">{selectedAppointment.service_type}</p>
                <p className="text-sm text-muted-foreground">{selectedAppointment.date}</p>
              </div>

              <StarRating
                label="Technical Skills"
                value={ratingData.skills_rating}
                onChange={(value) => setRatingData({ ...ratingData, skills_rating: value })}
              />

              <StarRating
                label="Conduct & Professionalism"
                value={ratingData.conduct_rating}
                onChange={(value) => setRatingData({ ...ratingData, conduct_rating: value })}
              />

              <StarRating
                label="Neatness of Work"
                value={ratingData.neatness_rating}
                onChange={(value) => setRatingData({ ...ratingData, neatness_rating: value })}
              />

              <div>
                <Label className="font-bold uppercase text-xs tracking-wide mb-2 block">Additional Comments (Optional)</Label>
                <Textarea
                  className="input-field min-h-[80px]"
                  placeholder="Share your experience..."
                  value={ratingData.comment}
                  onChange={(e) => setRatingData({ ...ratingData, comment: e.target.value })}
                  data-testid="rating-comment-input"
                />
              </div>

              <Button
                onClick={submitRating}
                className="btn-primary w-full h-12"
                data-testid="submit-rating-btn"
              >
                Submit Rating
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-foreground">Reschedule Appointment</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-6">
              <div className="bg-accent p-4 rounded-sm">
                <p className="text-sm font-medium text-muted-foreground mb-1">Current Appointment</p>
                <p className="font-heading font-bold text-foreground">{selectedAppointment.service_type}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedAppointment.date} at {selectedAppointment.time}
                </p>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-sm">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-yellow-700">
                    You can reschedule up to 24 hours before the appointment time.
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="reschedule-date" className="font-bold uppercase text-sm tracking-wide">New Date</Label>
                <Input
                  id="reschedule-date"
                  type="date"
                  required
                  data-testid="reschedule-date-input"
                  className="input-field mt-2 bg-blue-50 border-2 border-blue-300 focus:border-blue-500"
                  min={new Date().toISOString().split('T')[0]}
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="reschedule-time" className="font-bold uppercase text-sm tracking-wide">New Time</Label>
                <Input
                  id="reschedule-time"
                  type="time"
                  required
                  data-testid="reschedule-time-input"
                  className="input-field mt-2 bg-blue-50 border-2 border-blue-300 focus:border-blue-500"
                  value={rescheduleData.time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, time: e.target.value })}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={submitReschedule}
                  className="btn-primary flex-1 h-12"
                  data-testid="confirm-reschedule-btn"
                >
                  Confirm Reschedule
                </Button>
                <Button
                  onClick={() => setRescheduleDialogOpen(false)}
                  variant="outline"
                  className="btn-secondary flex-1 h-12"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Servicing Information Dialog */}
      <Dialog open={servicingInfoOpen} onOpenChange={setServicingInfoOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-foreground">Know More About Servicing</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[70vh] pr-4">
            <div className="space-y-6 text-sm">
              {/* Introduction */}
              <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-sm">
                <p className="text-yellow-800">
                  <strong>Terrains with Hot climates like Nigeria</strong> put extra stress on engine oil due to high ambient temperatures, dust, traffic idling, and stop-and-go driving. Because of that, manufacturer intervals should be shortened for safety.
                </p>
                <p className="text-yellow-700 mt-2">
                  Below are conservative, safe service intervals (not maximum marketing claims) that mechanics in hot regions commonly recommend.
                </p>
              </div>

              {/* Section Header */}
              <h3 className="text-xl font-heading font-bold text-primary flex items-center gap-2">
                🔧 Safe Engine Oil Change Intervals (Hot Weather Conditions)
              </h3>

              {/* 1. Full Synthetic */}
              <div className="bg-green-50 border-2 border-green-200 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-green-800 mb-2">1️⃣ Full Synthetic Oil</h4>
                <p className="text-green-700 italic mb-3">Best for extreme heat</p>
                <div className="grid md:grid-cols-2 gap-2 mb-3">
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Kilometres:</span> 7,000 – 8,000 km
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Time:</span> 4-5 months (even if mileage not reached)
                  </div>
                </div>
                <div className="mb-3">
                  <p className="font-bold text-green-800">🟢 Why:</p>
                  <ul className="list-disc list-inside text-green-700 ml-2">
                    <li>High resistance to heat breakdown</li>
                    <li>Better oxidation control</li>
                    <li>Maintains viscosity in extreme temperatures</li>
                  </ul>
                </div>
                <div className="bg-yellow-100 p-2 rounded">
                  <p className="text-yellow-800">⚠️ <strong>Reduce to 6,000 km if:</strong> Mostly city driving, frequent idling (traffic jams), dusty roads</p>
                </div>
              </div>

              {/* 2. Semi-Synthetic */}
              <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-yellow-800 mb-2">2️⃣ Semi-Synthetic (Synthetic Blend)</h4>
                <p className="text-yellow-700 italic mb-3">Balanced performance, mid-range option</p>
                <div className="grid md:grid-cols-2 gap-2 mb-3">
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Kilometres:</span> 4,000 – 6,000 km
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Time:</span> 3–4 months
                  </div>
                </div>
                <div className="mb-3">
                  <p className="font-bold text-yellow-800">🟡 Why:</p>
                  <ul className="list-disc list-inside text-yellow-700 ml-2">
                    <li>Partial heat resistance</li>
                    <li>Degrades faster than full synthetic</li>
                    <li>More sensitive to dust and fuel dilution</li>
                  </ul>
                </div>
                <div className="bg-orange-100 p-2 rounded">
                  <p className="text-orange-800">⚠️ <strong>Use 4,000 km</strong> for taxis, ride-hailing, or delivery vehicles.</p>
                </div>
              </div>

              {/* 3. Mineral Oil */}
              <div className="bg-red-50 border-2 border-red-200 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-red-800 mb-2">3️⃣ Mineral / Conventional (Non-Synthetic) Oil</h4>
                <p className="text-red-700 italic mb-3">Least suitable for hot climates</p>
                <div className="grid md:grid-cols-2 gap-2 mb-3">
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Kilometres:</span> 3,000 – 4,000 km
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Time:</span> 2 months
                  </div>
                </div>
                <div className="mb-3">
                  <p className="font-bold text-red-800">🔴 Why:</p>
                  <ul className="list-disc list-inside text-red-700 ml-2">
                    <li>Breaks down quickly under high heat</li>
                    <li>Loses viscosity faster</li>
                    <li>Forms sludge in hot engines</li>
                  </ul>
                </div>
                <div className="bg-red-100 p-2 rounded">
                  <p className="text-red-800">⚠️ <strong>In extreme heat (45°C+),</strong> do not exceed 3,000 km.</p>
                </div>
              </div>

              {/* 4. High-Mileage Mineral */}
              <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-orange-800 mb-2">4️⃣ High-Mileage Mineral Oil</h4>
                <p className="text-orange-700 italic mb-3">(Still mineral-based but improved additives)</p>
                <div className="grid md:grid-cols-2 gap-2 mb-3">
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Kilometres:</span> 4,000 – 5,000 km
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="font-bold">Time:</span> 3–4 months
                  </div>
                </div>
                <div>
                  <p className="font-bold text-orange-800">🟠 Why:</p>
                  <ul className="list-disc list-inside text-orange-700 ml-2">
                    <li>Better detergents and seal conditioners</li>
                    <li>Slightly better heat tolerance than basic mineral oil</li>
                  </ul>
                </div>
              </div>

              {/* Why Hot Weather */}
              <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-blue-800 mb-3">🌡️ Why Hot Weather Shortens Oil Life</h4>
                <ul className="list-disc list-inside text-blue-700 space-y-1">
                  <li>Faster oxidation</li>
                  <li>Oil thins and shears</li>
                  <li>Increased fuel dilution</li>
                  <li>Dust contamination</li>
                  <li>Longer idling times</li>
                </ul>
              </div>

              {/* Brake Fluid Section */}
              <h3 className="text-xl font-heading font-bold text-primary flex items-center gap-2 mt-6">
                🛑 Brake Fluid
              </h3>

              <div className="bg-red-50 border-2 border-red-200 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-red-800 mb-2">When to Change</h4>
                <div className="grid md:grid-cols-2 gap-2 mb-4">
                  <div className="bg-white p-3 rounded">
                    <span className="font-bold">Time:</span> Every 2–3 years
                  </div>
                  <div className="bg-white p-3 rounded">
                    <span className="font-bold">Distance:</span> Every 30,000–45,000 miles (50,000–70,000 km)
                  </div>
                </div>

                <div className="mb-4">
                  <p className="font-bold text-red-800 mb-2">Why Change Brake Fluid?</p>
                  <p className="text-red-700 text-sm mb-2">
                    Brake fluid absorbs moisture over time (it&apos;s hygroscopic). Water lowers its boiling point and can cause:
                  </p>
                  <ul className="list-disc list-inside text-red-700 ml-2 text-sm space-y-1">
                    <li>Spongy brake pedal</li>
                    <li>Reduced braking performance</li>
                    <li>Internal corrosion of brake components</li>
                  </ul>
                </div>

                <div className="bg-red-100 p-3 rounded">
                  <p className="font-bold text-red-800 mb-2">⚠️ Signs It Needs Changing:</p>
                  <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                    <li>Brake pedal feels soft or spongy</li>
                    <li>Brake warning light comes on</li>
                    <li>Fluid looks dark or dirty instead of clear/light amber</li>
                  </ul>
                </div>
              </div>

              {/* Engine Coolant Section */}
              <h3 className="text-xl font-heading font-bold text-primary flex items-center gap-2 mt-6">
                ❄️ Engine Coolant (Antifreeze)
              </h3>

              <div className="bg-cyan-50 border-2 border-cyan-200 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-cyan-800 mb-2">When to Change</h4>
                <p className="text-cyan-700 text-sm mb-3">This depends on the type:</p>
                
                <div className="space-y-3 mb-4">
                  <div className="bg-white p-3 rounded border-l-4 border-green-500">
                    <p className="font-bold text-foreground">Conventional (green) coolant:</p>
                    <p className="text-sm text-muted-foreground">👉 Every 2–3 years or 30,000 miles (50,000 km)</p>
                  </div>
                  <div className="bg-white p-3 rounded border-l-4 border-orange-500">
                    <p className="font-bold text-foreground">Long-life / Extended-life coolant (OAT, HOAT, etc.):</p>
                    <p className="text-sm text-muted-foreground">👉 Every 5 years or 100,000–150,000 miles (160,000–240,000 km)</p>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="font-bold text-cyan-800 mb-2">Why Change Coolant?</p>
                  <p className="text-cyan-700 text-sm mb-2">Over time, coolant loses its:</p>
                  <ul className="list-disc list-inside text-cyan-700 ml-2 text-sm space-y-1">
                    <li>Corrosion protection</li>
                    <li>Ability to control engine temperature</li>
                  </ul>
                  <p className="text-cyan-700 text-sm mt-2">Old coolant can cause:</p>
                  <ul className="list-disc list-inside text-cyan-700 ml-2 text-sm space-y-1">
                    <li>Overheating</li>
                    <li>Radiator and water pump damage</li>
                    <li>Rust inside the cooling system</li>
                  </ul>
                </div>

                <div className="bg-cyan-100 p-3 rounded">
                  <p className="font-bold text-cyan-800 mb-2">⚠️ Signs It Needs Changing:</p>
                  <ul className="list-disc list-inside text-cyan-700 text-sm space-y-1">
                    <li>Coolant looks rusty, brown, or cloudy</li>
                    <li>Engine runs hotter than normal</li>
                    <li>Sweet smell or visible leaks</li>
                    <li>Low coolant level repeatedly</li>
                  </ul>
                </div>
              </div>

              {/* Safety Tips */}
              <div className="bg-primary/10 border-2 border-primary/30 p-4 rounded-sm">
                <h4 className="font-heading font-bold text-lg text-primary mb-3">🛠️ Additional Safety Tips for Hot Climates</h4>
                <ul className="list-disc list-inside text-foreground space-y-2">
                  <li><strong>Always change the oil filter</strong> with every oil change</li>
                  <li>Use <strong>manufacturer-approved viscosity</strong> (often 5W-30 or 10W-40 in hot regions)</li>
                  <li><strong>Check oil every 1,000 km</strong></li>
                  <li>Consider <strong>synthetic oil for turbocharged</strong> or high-compression engines</li>
                  <li><strong>Check coolant level</strong> monthly and top up if needed</li>
                  <li><strong>Inspect brake fluid</strong> during every service visit</li>
                </ul>
              </div>

              {/* Close Button */}
              <div className="pt-4">
                <Button
                  onClick={() => setServicingInfoOpen(false)}
                  className="btn-primary w-full h-12"
                >
                  Got It!
                </Button>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Invite Friends Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold text-foreground">Invite Friends</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">Share Excel Era Auto Care with your friends and family!</p>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handleShare('whatsapp')}
                className="bg-green-500 hover:bg-green-600 text-white font-bold h-12"
                data-testid="share-whatsapp"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </Button>
              
              <Button
                onClick={() => handleShare('facebook')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-12"
                data-testid="share-facebook"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </Button>
              
              <Button
                onClick={() => handleShare('twitter')}
                className="bg-black hover:bg-gray-800 text-white font-bold h-12"
                data-testid="share-twitter"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X (Twitter)
              </Button>
              
              <Button
                onClick={() => handleShare('sms')}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold h-12"
                data-testid="share-sms"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                SMS
              </Button>
              
              <Button
                onClick={() => handleShare('email')}
                className="bg-red-500 hover:bg-red-600 text-white font-bold h-12"
                data-testid="share-email"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                Email
              </Button>
              
              <Button
                onClick={() => handleShare('copy')}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold h-12"
                data-testid="share-copy"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy Link
              </Button>
            </div>
            
            {navigator.share && (
              <Button
                onClick={() => handleShare('native')}
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 mt-2"
                data-testid="share-native"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                Share via Phone Contacts
              </Button>
            )}
            
            <div className="bg-accent p-4 rounded-sm mt-4">
              <p className="text-sm text-muted-foreground font-medium">Share this message:</p>
              <p className="text-sm mt-2 p-2 bg-white rounded border">{INVITE_MESSAGE}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}